import { Avatar, Input, Select, Space, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { capitalize } from "lodash";
import { useEffect } from "react";
import { useAppSelector } from "../../../../app/store";
import CaretDownIcon from "../../../../assets/icons/CaretDownIcon";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  useGetAllDepartmentQuery,
  useGetAllEmployeesQuery,
  useGetSubDepartmentByIdGroupChatQuery,
  useGiveReportDownloadAccessMutation,
} from "../../../../features/superAdmin/employeeService/employeeManagementService";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import { toast } from "../../../../lib/toast";
import { Employee, GetAllEmployeesParamsType } from "../../../../types";

const ManagePermission = ({
  id,
  isEnabled,
}: {
  id: number;
  isEnabled: boolean;
}) => {
  // ------ Mutations ------
  const [
    managePermission,
    {
      isLoading: managing,
      isError: isManageError,
      error: manageError,
      isSuccess,
    },
  ] = useGiveReportDownloadAccessMutation();

  useEffect(() => {
    if (!managing && isSuccess) {
      toast(
        "success",
        !isEnabled ? "Permission enabled." : "Permission disabled",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managing, isSuccess]);

  useError(isManageError, manageError, "Report download access failed.");
  return (
    <Space className="flex items-center justify-between">
      <span className="text-sm font-medium">
        {isEnabled ? "Enabled" : "Disabled"}
      </span>
      <Switch
        checked={isEnabled}
        onChange={() => {
          managePermission({ data: "", id });
        }}
        loading={managing}
        size="default"
      />
    </Space>
  );
};

export default function EmployeesContent() {
  const { role } = useAppSelector((state) => state.auth);

  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleFilter,
    handlePagination,
    handleSearchEnter,
  } = useTable<GetAllEmployeesParamsType>({
    defaultFilter: {
      status: undefined,
    },
  });

  // ------ Queries ------
  const {
    isLoading,
    isError,
    error,
    data: employees,
  } = useGetAllEmployeesQuery({
    params: {
      ...filter,
      page,
      search: keyword,
      skip: undefined,
      sort: "createdAt:desc",
      status: filter.status === "all" ? undefined : filter.status,
    },
  });
  const {
    isError: isDeptError,
    error: deptError,
    data: departments,
  } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100,
      status: "active",
    },
  });
  const {
    isError: isSubDeptError,
    error: subDeptError,
    data: subDepartments,
  } = useGetSubDepartmentByIdGroupChatQuery(filter?.department as number, {
    skip: !filter.department,
  });

  // ------ Errors ------
  useError(isError, error, "Get all user list fail");
  useError(isDeptError, deptError, "Get all department list fail");
  useError(isSubDeptError, subDeptError, "Get all sub department list fail");

  // ------ Table Columns ------
  const columns: ColumnsType<Employee> = [
    {
      title: "Employee ID",
      dataIndex: "officialInfo",
      render: (val) => val?.employee_id ?? "n/a",
    },
    {
      title: "Employee Name",
      render: (val) => {
        return (
          <div className="flex gap-2 items-center">
            <Avatar src={val?.personalInfo?.pro_pic} />
            <div>{val?.officialInfo?.name}</div>
          </div>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "officialInfo",
      render: (val) => val?.department?.name ?? "n/a",
    },
    {
      title: "Sub-Department",
      dataIndex: "officialInfo",
      render: (val) => val?.sub_department?.name ?? "n/a",
    },
    {
      title: "Email",
      dataIndex: "officialInfo",
      render: (val) => val?.email ?? "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <div
          className={`status ${
            status === "active" ? "status-success" : "status-danger"
          }`}
        >
          {capitalize(status)}
        </div>
      ),
    },
    ...(role === "super_admin" || role === "Admin"
      ? [
          {
            key: "action",
            title: "Report Download Access",
            align: "right" as const,
            render: ({ id, can_download_reports }: Employee) => {
              const isEnabled = !!can_download_reports;

              return (
                <div className="flex items-center justify-end">
                  <ManagePermission id={id} isEnabled={isEnabled} key={id} />
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div>
      <CustomTable
        className="designationTable"
        limit={limit}
        page={page}
        columns={columns}
        dataSource={employees?.data?.employees}
        loading={isLoading}
        total={employees?.data?.total || 0}
        handlePagination={handlePagination}
        filterSection={
          <>
            <div className="flex gap-2 items-center">
              <div className="text-nowrap">Total Rows</div>
              <Select
                placeholder="10"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                style={{ width: 70 }}
                onChange={handleLimit}
                options={[
                  { label: "10", value: 10 },
                  { label: "20", value: 20 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 },
                  { label: "1000", value: 1000 },
                ]}
              />
              <div className="text-nowrap">
                of {employees?.data?.total || 0}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={keyword || undefined}
                onChange={(e) => handleSearchEnter(e.currentTarget.value)}
                placeholder="Search"
                size="middle"
              />
              <Select
                className="!min-w-40"
                placeholder="Department"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                value={filter?.department ?? undefined}
                onChange={(value) => {
                  handleFilter("department", value);
                  handleFilter("sub_department", undefined);
                }}
                options={departments?.data.map((dep: any) => ({
                  label: dep.name,
                  value: dep.id,
                }))}
                allowClear
              />
              <Select
                className="!min-w-40"
                placeholder="Sub Department"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                value={filter?.sub_department ?? undefined}
                onChange={(value) => handleFilter("sub_department", value)}
                options={subDepartments?.data.map((dep: any) => ({
                  label: dep.name,
                  value: dep.id,
                }))}
                allowClear
                disabled={!filter.department}
              />
              <Select
                className="!min-w-40"
                placeholder="Status"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                value={filter.status ?? undefined}
                onChange={(value) => handleFilter("status", value)}
                options={[
                  { label: "All", value: "all" },
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
                allowClear
              />
            </div>
          </>
        }
      />
    </div>
  );
}
